/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';

import { InventoryTag, Icon, Button, toTitleCase } from 'sarsaparilla';

import type { SearchResultItem } from '../../api/search.types';
import { useAppDispatch } from '../../redux/redux-helpers';
import { updateSearchCriterias } from '../../actions/search';
import { searchDate } from '../../utils/dateUtils';
import { CAL_DATE_FORMAT } from '../../constants';

function decodeHTML(val: string | null | undefined) {
    let html = val || '';

    if (html) {
        html = html.replace('&#257;', 'ā');
        html = html.replace('&#39;', "'");
        html = html.replace('&amp;', '&');
        html = html.replace('&nbsp;', ' ');
        html = html.replace('&lt;', '<');
        html = html.replace('&gt;', '>');
    }
    return html;
}

export function formatLocationName(name: string) {
    return toTitleCase(decodeHTML(name));
}

interface CardInventoryTagProps {
    item: SearchResultItem;
}

export function CardInventoryTag({ item }: CardInventoryTagProps) {
    const memoizedPills = React.useMemo(() => {
        if (!item?.entity_type) return [];

        const pills: React.ReactNode[] = [];

        switch (item.entity_type) {
            case 'recarea': {
                pills.push(<InventoryTag.InventoryRecArea hasText key="recarea" />);
                break;
            }
            case 'permit': {
                if (item.permit_type === 'hunting') {
                    pills.push(
                        <InventoryTag.InventoryHuntingPermit
                            hasText
                            key="hunting-permit"
                        />
                    );
                } else {
                    pills.push(<InventoryTag.InventoryPermit hasText key="permit" />);
                }
                break;
            }
            case 'vehiclepermit':
                pills.push(<InventoryTag.InventoryPermit hasText key="permit" />);
                break;
            case 'campground':
                pills.push(...getCampgroundPills(item));
                break;
            case 'venuereservations':
            case 'venuereservations_venue':
                pills.push(...getVenuePills(item));
                break;
            case 'ticket':
            case 'ticketfacility':
            case 'tour':
            case 'timedentry':
            case 'timedentry_tour':
                pills.push(<InventoryTag.InventoryTicket hasText key="ticket" />);
                break;
            case 'activitypass':
                pills.push(
                    <InventoryTag.InventoryActivityPass
                        hasText
                        name={item?.activity_category_type || 'Activity Pass'}
                        key="activityPass"
                    />
                );
                break;
            case 'treepermit':
                pills.push(
                    <InventoryTag.InventoryTreePermits hasText key="treePermit" />
                );
                break;
            case 'giftcard':
                pills.push(<InventoryTag.InventoryGiftCard hasText key="giftCard" />);
                break;
            default:
                pills.push(<InventoryTag.InventoryPOI hasText key="poi" />);
        }

        if (item.park_pass_facility_id) {
            pills.push(<InventoryTag.InventorySitePass hasText key="parkPass" />);
        }

        if (item.has_lottery) {
            pills.push(<InventoryTag.InventoryLottery hasText key="lottery" />);
        }

        return pills;
    }, [item]);

    return (
        <div className="pills-list" role={memoizedPills.length > 1 ? 'list' : undefined}>
            {/* if there is only one list item, it becomes a span and parent is a div */}
            {memoizedPills.length === 1
                ? React.cloneElement(memoizedPills[0] as React.ReactElement, {
                      isSpanItem: true,
                  })
                : memoizedPills}
        </div>
    );
}

interface CardPriceProps {
    item: SearchResultItem;
}

export function CardPrice({ item }: CardPriceProps) {
    const { priceRangeLabel, priceRangeUnit, priceRangeAriaLabel } = getPriceRange(item);

    if (!priceRangeLabel) return null;

    return (
        <div className="card-price">
            <span className="price-range-label" aria-hidden="true">
                {priceRangeLabel}
            </span>

            {priceRangeUnit ? (
                <span className="price-range-unit" aria-hidden="true">
                    {priceRangeUnit}
                </span>
            ) : null}

            <div className="rec-sr-only">{priceRangeAriaLabel}</div>
        </div>
    );
}

export function getCardRating(item: SearchResultItem) {
    if (!item.average_rating || !item.number_of_ratings) {
        return {
            averageRating: undefined,
            reviewCount: undefined,
            ratingAriaLabel: undefined,
        };
    }

    const reviewFormatter = new Intl.NumberFormat('en-US', {
        maximumFractionDigits: 1,
    });

    const reviewCountFormatter = new Intl.NumberFormat('en-US', {
        maximumFractionDigits: 0,
    });

    const averageRating = reviewFormatter.format(item.average_rating);
    const reviewCount = reviewCountFormatter.format(item.number_of_ratings);
    const ratingAriaLabel = `Rating:${averageRating} out of 5 stars ${item.number_of_ratings ? ` based on ${reviewCount} reviews.` : ''}`;
    const ratingAriaLabelShort = `${averageRating} stars`;

    return { averageRating, reviewCount, ratingAriaLabel, ratingAriaLabelShort };
}

interface CardStarRatingProps {
    item: SearchResultItem;
}

export function CardStarRating({ item }: CardStarRatingProps) {
    if (!item.average_rating || !item.number_of_ratings) return null;

    const { averageRating, reviewCount, ratingAriaLabel } = getCardRating(item);

    return (
        <div className="card-rating">
            <Icon iconName="star" size="sm" className="star-icon" aria-hidden="true" />

            <span className="rating-value" aria-hidden="true">
                {averageRating}
            </span>

            <span className="review-count" aria-hidden="true">
                {`(${reviewCount})`}
            </span>

            <div className="rec-sr-only">{ratingAriaLabel}</div>
        </div>
    );
}

interface CardFooterProps {
    item: SearchResultItem;
}

export function CardFooter({ item }: CardFooterProps) {
    if (item.park_pass_facility_id) {
        return (
            <div className="card-footer has-pass">
                <Button
                    size="xs"
                    appearance="primary"
                    href={`/sitepass/${item.park_pass_facility_id}`}
                    aria-label={`Buy pass for ${item.name}`}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Buy a Pass
                </Button>
            </div>
        );
    }

    // if there's no rating or price, return null
    if (!item.average_rating && !item.price_range) {
        return <div className="card-footer-empty" />;
    }

    return (
        <div className="card-footer">
            <CardStarRating item={item} />
            <CardPrice item={item} />
        </div>
    );
}

// Helper for campground pills
function getCampgroundPills(item: SearchResultItem) {
    if (
        item.campsite_type_of_use &&
        item.campsite_type_of_use.length > 0 &&
        item.campsite_type_of_use[0] !== 'na'
    ) {
        return item.campsite_type_of_use
            .map((type, idx) => {
                if (type === 'Day') {
                    return <InventoryTag.InventoryDayUse hasText key={idx} />;
                }

                if (type === 'Overnight') {
                    return <InventoryTag.InventoryCamping hasText key={idx} />;
                }

                return null; // remove unsupported types
            })
            .filter(Boolean); // dump nulls
    }
    // Default: assume a campground
    return [<InventoryTag.InventoryCamping hasText key={0} />];
}

// Helper for venue pills
function getVenuePills(item: SearchResultItem) {
    if (item.venue_tags && item.venue_tags.length > 0) {
        return item.venue_tags.map((type, idx) => {
            switch (type) {
                case 'wedding-ceremony':
                    return <InventoryTag.InventoryVenueWedding hasText key={idx} />;
                case 'large-reception':
                    return (
                        <InventoryTag.InventoryVenueLargeReception hasText key={idx} />
                    );
                case 'small-gathering':
                    return (
                        <InventoryTag.InventoryVenueSmallGathering hasText key={idx} />
                    );
                case 'golfing':
                    return <InventoryTag.InventoryVenueGolfing hasText key={idx} />;
                case 'shooting':
                    return <InventoryTag.InventoryVenueShooting hasText key={idx} />;
                case 'hunting':
                    return <InventoryTag.InventoryVenueHunting hasText key={idx} />;
                case 'athletic-activity':
                    return (
                        <InventoryTag.InventoryVenueAthleticActivity hasText key={idx} />
                    );
                case 'other':
                    return <InventoryTag.InventoryVenueOther hasText key={idx} />;
                default:
                    return (
                        <InventoryTag.InventoryVenueOther name={type} hasText key={idx} />
                    );
            }
        });
    }
    // Default: assume other
    return [<InventoryTag.InventoryVenueOther hasText key={0} />];
}

interface CardLocationProps {
    parent?: string;
    distance?: string;
    city?: string;
    state?: string;
}

export function CardLocation({
    parent,
    distance: distanceProp,
    city,
    state,
}: CardLocationProps) {
    const near = city && state ? `Near ${city}, ${getStateAbbreviation(state)}` : null;

    const formatter = new Intl.NumberFormat('en-US', {
        maximumFractionDigits: 0,
    });

    const distance = kilometersToMiles(distanceProp);
    const texts: string[] = [];

    if (parent) {
        texts.push(parent);
    }
    if (near) {
        texts.push(near);
    }

    if (distance !== null) {
        texts.push(`${formatter.format(distance)} miles away`);
    }

    return (
        <div className="card-location">
            <div className="text-xs">{`${texts.join(' • ')}`}</div>
        </div>
    );
}

export function useOnSearchResultCardLinkClick() {
    const dispatch = useAppDispatch();

    return React.useCallback(
        (event: React.MouseEvent<HTMLAnchorElement>, item: SearchResultItem) => {
            const dates = getDatesValue(item);

            if (dates) {
                const [startDate, endDate] = dates;
                dispatch(
                    updateSearchCriterias({
                        checkin_time: startDate.format(CAL_DATE_FORMAT),
                        checkout_time: endDate.format(CAL_DATE_FORMAT),
                    })
                );
            }

            // clear transient camping data that causes filters reset
            window.localStorage.removeItem('last-visited-campground');
            const url = event?.currentTarget?.href;

            if (url) {
                event.preventDefault();
                window.open(url, '_blank');
            }
        },
        [dispatch]
    );
}

function getDatesValue(item: SearchResultItem) {
    const availableDates = item?.available_dates?.[0];
    const startDateValue = availableDates?.start_date
        ? searchDate(availableDates?.start_date).utc()
        : null;
    const endDateValue = availableDates?.end_date
        ? searchDate(availableDates?.end_date).utc()
        : null;

    if (!startDateValue?.isValid() || !endDateValue?.isValid()) {
        return null;
    }

    return [startDateValue, endDateValue];
}

function getStateAbbreviation(name: string): string | null {
    const states: { [key: string]: string } = {
        alabama: 'AL',
        alaska: 'AK',
        arizona: 'AZ',
        arkansas: 'AR',
        california: 'CA',
        colorado: 'CO',
        connecticut: 'CT',
        delaware: 'DE',
        florida: 'FL',
        georgia: 'GA',
        hawaii: 'HI',
        idaho: 'ID',
        illinois: 'IL',
        indiana: 'IN',
        iowa: 'IA',
        kansas: 'KS',
        kentucky: 'KY',
        louisiana: 'LA',
        maine: 'ME',
        maryland: 'MD',
        massachusetts: 'MA',
        michigan: 'MI',
        minnesota: 'MN',
        mississippi: 'MS',
        missouri: 'MO',
        montana: 'MT',
        nebraska: 'NE',
        nevada: 'NV',
        'new hampshire': 'NH',
        'new jersey': 'NJ',
        'new mexico': 'NM',
        'new york': 'NY',
        'north carolina': 'NC',
        'north dakota': 'ND',
        ohio: 'OH',
        oklahoma: 'OK',
        oregon: 'OR',
        pennsylvania: 'PA',
        'rhode island': 'RI',
        'south carolina': 'SC',
        'south dakota': 'SD',
        tennessee: 'TN',
        texas: 'TX',
        utah: 'UT',
        vermont: 'VT',
        virginia: 'VA',
        washington: 'WA',
        'west virginia': 'WV',
        wisconsin: 'WI',
        wyoming: 'WY',
        'district of columbia': 'DC',
        'american samoa': 'AS',
        guam: 'GU',
        'northern mariana islands': 'MP',
        'puerto rico': 'PR',
        'us virgin islands': 'VI',
    };

    const normalized = name.trim().toLowerCase();
    return states[normalized] || null;
}

function kilometersToMiles(km?: number | string): number | null {
    if (km === undefined || km === null) {
        return null;
    }

    const numericKm = typeof km === 'string' ? parseFloat(km) : km;

    if (Number.isNaN(numericKm)) {
        return null;
    }

    const milesPerKilometer = 0.621371;
    return numericKm * milesPerKilometer;
}

export function getPriceRange(item: SearchResultItem) {
    let priceRangeLabel: string | undefined;
    let priceRangeUnit: string | undefined;
    let priceRangeAriaLabel: string | undefined;

    const pricedEntityTypes = ['campground', 'venuereservations'];
    const isPricedEntityType = pricedEntityTypes.includes(item?.entity_type ?? '');
    const isReservable = item?.reservable ?? false;

    if (isPricedEntityType && isReservable && item.price_range) {
        const minAmount = item.price_range.amount_min;
        const maxAmount = item.price_range.amount_max;

        if (minAmount && maxAmount) {
            // USD currency formatter
            const usdFormatter = new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 0,
            });

            if (minAmount > -1) {
                priceRangeLabel =
                    minAmount === maxAmount
                        ? usdFormatter.format(minAmount)
                        : `${usdFormatter.format(minAmount)} – ${usdFormatter.format(maxAmount)}`;

                priceRangeAriaLabel = priceRangeLabel;
            }
        }

        if (item.price_range?.per_unit) {
            priceRangeUnit = ` / ${item.price_range.per_unit}`;
            priceRangeAriaLabel += ` per ${item.price_range.per_unit}`;
        }
    }

    return { priceRangeLabel, priceRangeUnit, priceRangeAriaLabel };
}

// cSpell:ignore Criterias sitepass checkin recarea venuereservations venuereservations_venue vehiclepermit ticketfacility timedentry timedentry_tour giftcard treepermit activitypass
